import { getCooperlyAppBaseUrl } from "@/lib/app-base-url";

export type RolloutPlan = {
  name: string;
  price: string;
  duration: string;
  description: string;
  emphasized: string;
  features: readonly string[];
  primaryCta: {
    label: string;
    href: string;
  };
  secondaryCta: {
    label: string;
    href: string;
  };
};

export type PaidPlan = {
  name: string;
  planCode: "solo" | "multi_5";
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyNote: string;
  yearlyNote: string;
  priceSuffix: string;
  features: readonly string[];
  maxTeamMembers: number;
  teamLimitLabel: string;
};

export const rolloutPlan = {
  name: "Guided Rollout",
  price: "£0",
  duration: "/ 4 weeks",
  description:
    "We roll Cooperly.ai out with you, so your team gets it and doesn't roll their eyes.",
  emphasized: "No commitment to a paid plan required.",
  features: [
    "Team onboarding (done with you)",
    "4 weekly rollout calls (30 mins)",
    "Profiles Review",
    "First Pulse + Fundamentals set-up",
  ],
  primaryCta: {
    label: "Apply for guided rollout",
    href: "https://docs.google.com/forms/d/e/1FAIpQLSdfoPVan6XMK2p3_iHPwqoQ76VYaKYaoefsocGodsA9-dB_ag/viewform?usp=dialog",
  },
  secondaryCta: {
    label: "Book a demo",
    href: "https://cal.eu/cooperly/demo",
  },
} as const satisfies RolloutPlan;

export const paidPlans = [
  {
    name: "For Teams",
    planCode: "solo",
    description:
      "Keep working profiles, weekly signals, and the next action together for one team at one flat price.",
    monthlyPrice: 25,
    yearlyPrice: 20,
    monthlyNote: "Billed monthly",
    yearlyNote: "Billed yearly",
    priceSuffix: "/ month",
    features: [
      "Coop Profiles, Pulse and Fundamentals",
      "Roadmap, MCP and approved integrations",
      "1 team included",
      "Up to 10 team members",
      "1-year history",
      "Email support",
    ],
    maxTeamMembers: 10,
    teamLimitLabel: "1 team",
  },
  {
    name: "For Companies",
    planCode: "multi_5",
    description:
      "Keep people context and the review rhythm connected across several teams as the company grows.",
    monthlyPrice: 99,
    yearlyPrice: 79,
    monthlyNote: "Billed monthly",
    yearlyNote: "Billed yearly",
    priceSuffix: "/ month",
    features: [
      "Coop Profiles, Pulse and Fundamentals",
      "Roadmap, MCP and approved integrations",
      "Up to 5 teams",
      "Up to 50 team members",
      "3-year history",
      "Monthly review call (30 mins)",
    ],
    maxTeamMembers: 50,
    teamLimitLabel: "Up to 5 teams",
  },
] as const satisfies readonly PaidPlan[];

export function getDiscountPercent(monthlyPrice: number, yearlyPrice: number) {
  return Math.round(((monthlyPrice - yearlyPrice) / monthlyPrice) * 100);
}

export function getTrialSignupHref(
  planCode: PaidPlan["planCode"],
  billingCycle: "monthly" | "yearly",
  acquisitionCtaId = "pricing_trial",
) {
  const params = new URLSearchParams({
    plan: planCode,
    billing_cycle: billingCycle,
    utm_source: "cooperly_ai_site",
    utm_medium: "website",
    utm_campaign: "pricing_trial",
    acquisition_campaign: "pricing_trial",
    acquisition_cta_id: acquisitionCtaId,
  });

  return `${getCooperlyAppBaseUrl()}/register?${params.toString()}`;
}

export function getNeutralTrialSignupHref(acquisitionCtaId = "home_hero_start_trial") {
  const params = new URLSearchParams({
    utm_source: "cooperly_ai_site",
    utm_medium: "website",
    utm_campaign: "hero_trial",
    acquisition_campaign: "hero_trial",
    acquisition_cta_id: acquisitionCtaId,
  });

  return `${getCooperlyAppBaseUrl()}/register?${params.toString()}`;
}

export const maxPlanDiscountPercent = Math.max(
  ...paidPlans.map((plan) => getDiscountPercent(plan.monthlyPrice, plan.yearlyPrice)),
);
